import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Upload, User as UserIcon } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";
const API_ROOT = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MyProfile() {
  const [me, setMe] = useState(null);
  const [f, set] = useState({ name: "", phone: "", position: "", bio: "", qualifications: "" });
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [avatarTs, setAvatarTs] = useState(Date.now());

  useEffect(() => {
    api.get("/me/profile").then((r) => {
      setMe(r.data);
      set({
        name: r.data.name || "", phone: r.data.phone || "",
        position: r.data.position || "", bio: r.data.bio || "",
        qualifications: r.data.qualifications || "",
      });
    });
  }, []);

  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.patch("/me/profile", f);
      setMe(res.data);
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pw || pw.length < 4) { toast.error("Password too short"); return; }
    setPwBusy(true);
    try {
      await api.patch(`/admin/users/${me.id}`, { password: pw });
      toast.success("Password changed");
      setPw("");
    } catch (e) {
      // fall back to profile endpoint (which doesn't support password) — this only works for admins on their own account
      toast.error("Only admins can change passwords from here. Ask an admin to reset yours.");
    }
    finally { setPwBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/me/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      setAvatarTs(Date.now());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };

  if (!me) return <div className="p-8 text-njas-muted">Loading…</div>;

  const photoSrc = me.photo_path ? `${API_ROOT}/users/${me.id}/photo?v=${avatarTs}` : null;

  return (
    <div className="fade-in max-w-3xl" data-testid="my-profile-page">
      <div className="eyebrow text-njas-sage-hover text-[10px]">Account</div>
      <h2 className="font-serif text-3xl text-njas-navy mb-6">My Profile</h2>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] p-6 flex items-center gap-6 mb-6">
        <div className="w-24 h-24 rounded-full bg-njas-sage-tint text-njas-sage-hover overflow-hidden flex items-center justify-center">
          {photoSrc
            ? <img src={photoSrc} alt="me" className="w-full h-full object-cover" />
            : <UserIcon className="w-10 h-10" />}
        </div>
        <div className="flex-1">
          <div className="font-serif text-2xl text-njas-navy">{me.name}</div>
          <div className="text-sm text-njas-muted">{me.email} · <span className="capitalize">{(me.role || "").replace("_", " ")}</span></div>
          <label className="mt-2 inline-flex items-center gap-2 text-sm text-njas-navy hover:underline cursor-pointer" data-testid="upload-avatar-label">
            <Upload className="w-4 h-4" /> Upload photo
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="upload-avatar-input" />
          </label>
        </div>
      </div>

      <form onSubmit={save} className="rounded-xl bg-white border border-[var(--njas-border)] p-6 space-y-4" data-testid="profile-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <FL label="Name"><input value={f.name} onChange={upd("name")} className={inputCls} /></FL>
          <FL label="Phone"><input value={f.phone} onChange={upd("phone")} className={inputCls} /></FL>
          <FL label="Position / job title"><input value={f.position} onChange={upd("position")} placeholder="e.g. Senior Social Worker, Peer Mentor" className={inputCls} /></FL>
          <FL label="Qualifications"><input value={f.qualifications} onChange={upd("qualifications")} placeholder="e.g. BSW, AASW registered" className={inputCls} /></FL>
        </div>
        <FL label="Bio / About me"><textarea rows={4} value={f.bio} onChange={upd("bio")} className={inputCls} placeholder="A short bio visible to your teammates." /></FL>
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </form>

      {me.role === "admin" && (
        <form onSubmit={changePassword} className="mt-6 rounded-xl bg-white border border-[var(--njas-border)] p-6 space-y-3" data-testid="password-form">
          <div className="eyebrow text-njas-sage-hover text-[10px]">Security</div>
          <div className="font-serif text-lg text-njas-navy">Change my password</div>
          <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className={inputCls} data-testid="new-password-input" />
          <div className="flex justify-end">
            <button type="submit" disabled={pwBusy || !pw} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
              {pwBusy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function FL({ label, children }) {
  return <label className="block"><span className="text-xs font-medium text-njas-navy block mb-1.5 uppercase tracking-wider">{label}</span>{children}</label>;
}
